/**
 * RepairsPanel — v0.4 §1.5.1 §C.5 uniform repair-documentation workflow UI.
 *
 * - Repairs tab: open + historical repair orders with status chips.
 * - Resolve modal: requires a resolution summary AND ≥1 receipt/photo
 *   (client-side mirror of the server's §396 evidence rule §C.4).
 * - Create repair order: fixer-type selector is a RECORD field, never a role —
 *   In-house (optionally pick the assigned fixer from company users) or
 *   External shop (free-text shop name). No role picker anywhere.
 * - Driver path: "Request repair" from a reported DEFECT (source: DEFECT).
 */
import { useState, useEffect, useCallback } from 'react';
import { Wrench, X, Plus, CheckCircle2, Camera, FileText } from 'lucide-react';
import { dotApi } from '@/utils/api';

export interface RepairOrder {
  id: number;
  vehicleId: number;
  defectId: number | null;
  source: string;
  status: 'OPEN' | 'OUT_OF_SERVICE' | 'IN_PROGRESS' | 'RESOLVED';
  outOfService: boolean;
  fixerType: 'IN_HOUSE' | 'EXTERNAL_SHOP';
  externalShopName?: string | null;
  assignedFixerUserId?: number | null;
  title: string;
  description?: string | null;
  repairNotes?: string | null;
  partsReceiptObjectKeys?: string[] | null;
  repairPhotoObjectKeys?: string[] | null;
  resolutionSummary?: string | null;
  createdAt: string;
}

const STATUS_CHIP: Record<RepairOrder['status'], string> = {
  OPEN: 'bg-gray-100 text-gray-700',
  OUT_OF_SERVICE: 'bg-red-100 text-red-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  RESOLVED: 'bg-green-100 text-green-700',
};

const evidenceCount = (o: RepairOrder) =>
  (o.partsReceiptObjectKeys?.length ?? 0) + (o.repairPhotoObjectKeys?.length ?? 0);

export function RepairsPanel({ user, vehicles }: { user: any; vehicles: { id: number; unitNumber: string }[] }) {
  const [orders, setOrders] = useState<RepairOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolveTarget, setResolveTarget] = useState<RepairOrder | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Admin/PM/CM (fixer-type is a record field, NOT a role — no role picker)
  const canManage = ['ADMIN', 'PROJECT_MANAGER', 'CONSTRUCTION_MANAGER'].includes(user?.role);
  // MED-3 (auditor): the assigned fixer can document/resolve their own order
  // (server object rule, dot.repair-authz.ts) even when not a manager — surface
  // the affordance; the server enforces the actual authorization.
  const isAssignedFixer = (o: RepairOrder) => user?.id != null && o.assignedFixerUserId === user.id;
  const canResolve = (o: RepairOrder) => canManage || isAssignedFixer(o);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await dotApi.listRepairOrders();
      setOrders(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load repair orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vehicleLabel = (id: number) => vehicles.find((v) => v.id === id)?.unitNumber ?? `#${id}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Repair orders</h2>
        {canManage && (
          <button onClick={() => setCreateOpen(true)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm flex items-center gap-1">
            <Plus className="h-4 w-4" /> New repair order
          </button>
        )}
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : orders.length === 0 ? (
        <p className="text-gray-500">No repair orders yet.</p>
      ) : (
        <div className="grid gap-3">
          {orders.map((o) => (
            <div key={o.id} className="bg-white p-4 rounded-lg shadow border border-gray-100">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-gray-900 flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-blue-500" /> {o.title}
                  </div>
                  <div className="text-sm text-gray-500">
                    Unit {vehicleLabel(o.vehicleId)} · {o.fixerType === 'EXTERNAL_SHOP' ? `External shop: ${o.externalShopName ?? '—'}` : 'In-house'}
                    {o.defectId != null && ` · from DVIR defect #${o.defectId}`}
                  </div>
                  {o.description && <p className="text-sm text-gray-600 mt-1">{o.description}</p>}
                  {o.repairNotes && <p className="text-xs text-gray-500 mt-1 whitespace-pre-line">{o.repairNotes}</p>}
                  {o.status === 'RESOLVED' && o.resolutionSummary && (
                    <p className="text-xs text-green-700 mt-1">Resolution: {o.resolutionSummary}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${STATUS_CHIP[o.status]}`}>{o.status.replace('_', ' ')}</span>
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <FileText className="h-3 w-3" /> {evidenceCount(o)} evidence
                  </span>
                  {canResolve(o) && o.status !== 'RESOLVED' && (
                    <button onClick={() => setResolveTarget(o)} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" /> Mark resolved
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {resolveTarget && <ResolveModal order={resolveTarget} onClose={() => setResolveTarget(null)} onDone={() => { setResolveTarget(null); load(); }} />}
      {createOpen && <CreateRepairModal vehicles={vehicles} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); load(); }} />}
    </div>
  );
}

/** Resolve modal — mirrors the server's §396 evidence rule client-side. */
function ResolveModal({ order, onClose, onDone }: { order: RepairOrder; onClose: () => void; onDone: () => void }) {
  const [summary, setSummary] = useState('');
  const [receipts, setReceipts] = useState<string[]>(order.partsReceiptObjectKeys ?? []);
  const [photos, setPhotos] = useState<string[]>(order.repairPhotoObjectKeys ?? []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const hasEvidence = receipts.length + photos.length >= 1;

  const upload = async (kind: 'receipt' | 'photo', file: File) => {
    // Audit HIGH-1/HIGH-2: upload the REAL file to the repair evidence endpoint
    // (server stores bytes + generates the objectKey). No fabricated fallback key —
    // evidence must be real stored files, not client-invented strings.
    try {
      setBusy(true);
      await dotApi.attachRepairEvidence(order.id, kind, file, file.name);
      const res = await dotApi.getRepairOrder(order.id);
      setReceipts(res.data?.partsReceiptObjectKeys ?? []);
      setPhotos(res.data?.repairPhotoObjectKeys ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!summary.trim()) return setError('A resolution summary is required.');
    if (!hasEvidence) return setError('At least one receipt or photo is required to resolve (49 CFR §396).');
    try {
      setBusy(true);
      await dotApi.resolveRepairOrder(order.id, { resolutionSummary: summary });
      onDone();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Resolve failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-20">
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-600" /> Mark resolved — {order.title}</h3>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Resolution summary (required)" rows={3} className="w-full border rounded-lg p-2 text-sm mb-2" />
        <div className="flex gap-3 mb-2">
          <label className="flex items-center gap-1 text-sm text-blue-600 cursor-pointer">
            <FileText className="h-4 w-4" /> Upload receipt
            <input type="file" hidden onChange={(e) => e.target.files?.[0] && upload('receipt', e.target.files[0])} />
          </label>
          <label className="flex items-center gap-1 text-sm text-blue-600 cursor-pointer">
            <Camera className="h-4 w-4" /> Upload photo
            <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && upload('photo', e.target.files[0])} />
          </label>
        </div>
        <p className={`text-xs mb-3 ${hasEvidence ? 'text-green-600' : 'text-amber-600'}`}>
          {receipts.length} receipt(s), {photos.length} photo(s) — at least one is required to resolve.
        </p>
        <button onClick={submit} disabled={busy || !summary.trim() || !hasEvidence} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
          Resolve order
        </button>
      </div>
    </div>
  );
}

/** Create repair order — fixer type is a RECORD field, never a role. */
function CreateRepairModal({ vehicles, onClose, onDone }: { vehicles: { id: number; unitNumber: string }[]; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    vehicleId: vehicles[0]?.id ?? 0,
    title: '',
    description: '',
    outOfService: false,
    fixerType: 'IN_HOUSE' as 'IN_HOUSE' | 'EXTERNAL_SHOP',
    externalShopName: '',
    assignedFixerUserId: '' as string,
  });
  const [users, setUsers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Company users for the in-house fixer picker (no role filter — fixer is not a role)
    import('@/utils/api').then(({ api }) => api.get('/assignments/eligible-workers').then((r: any) => setUsers(r.data?.data ?? r.data ?? [])).catch(() => {}));
  }, []);

  const submit = async () => {
    if (!form.title.trim()) return setError('A title is required.');
    if (!form.vehicleId) return setError('Select a vehicle.');
    if (form.fixerType === 'EXTERNAL_SHOP' && !form.externalShopName.trim()) return setError('External shop name is required for an external shop.');
    try {
      setBusy(true);
      await dotApi.createRepairOrder({
        vehicleId: form.vehicleId,
        title: form.title,
        description: form.description || undefined,
        outOfService: form.outOfService,
        fixerType: form.fixerType,
        externalShopName: form.fixerType === 'EXTERNAL_SHOP' ? form.externalShopName : undefined,
        assignedFixerUserId: form.fixerType === 'IN_HOUSE' && form.assignedFixerUserId ? Number(form.assignedFixerUserId) : undefined,
      });
      onDone();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create repair order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-20">
      <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><Wrench className="h-5 w-5 text-blue-500" /> New repair order</h3>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <select value={form.vehicleId} onChange={(e) => setForm({ ...form, vehicleId: Number(e.target.value) })} className="w-full border rounded-lg p-2 text-sm mb-2">
          {vehicles.map((v) => <option key={v.id} value={v.id}>Unit {v.unitNumber}</option>)}
        </select>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Air brake line replacement)" className="w-full border rounded-lg p-2 text-sm mb-2" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={2} className="w-full border rounded-lg p-2 text-sm mb-2" />
        {/* Fixer type: record field only — never a role picker (§C.5) */}
        <select value={form.fixerType} onChange={(e) => setForm({ ...form, fixerType: e.target.value as any })} className="w-full border rounded-lg p-2 text-sm mb-2">
          <option value="IN_HOUSE">In-house</option>
          <option value="EXTERNAL_SHOP">External shop</option>
        </select>
        {form.fixerType === 'IN_HOUSE' ? (
          <select value={form.assignedFixerUserId} onChange={(e) => setForm({ ...form, assignedFixerUserId: e.target.value })} className="w-full border rounded-lg p-2 text-sm mb-2">
            <option value="">Assigned fixer (optional)</option>
            {users.map((u: any) => <option key={u.id} value={u.id}>{u.firstName ? `${u.firstName} ${u.lastName}` : u.email}</option>)}
          </select>
        ) : (
          <input value={form.externalShopName} onChange={(e) => setForm({ ...form, externalShopName: e.target.value })} placeholder="Shop name (free text)" className="w-full border rounded-lg p-2 text-sm mb-2" />
        )}
        <label className="flex items-center gap-2 mb-3 text-sm">
          <input type="checkbox" checked={form.outOfService} onChange={(e) => setForm({ ...form, outOfService: e.target.checked })} />
          Take vehicle out of service (sets dispatch hold)
        </label>
        <button onClick={submit} disabled={busy} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">Create repair order</button>
      </div>
    </div>
  );
}

/** Driver path: "Request repair" from a reported defect (source: DEFECT). */
export function RequestRepairFromDefect({ vehicleId, defectId }: { vehicleId: number; defectId: number }) {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const request = async () => {
    try {
      setBusy(true);
      await dotApi.createRepairOrder({ vehicleId, defectId, title: `Repair requested — DVIR defect #${defectId}` });
      setDone(true);
    } finally {
      setBusy(false);
    }
  };
  if (done) return <p className="text-xs text-green-600 mt-2">Repair order requested.</p>;
  return (
    <button onClick={request} disabled={busy} className="text-sm text-blue-600 hover:underline mt-2 flex items-center gap-1">
      <Wrench className="h-4 w-4" /> Request repair
    </button>
  );
}
